import "./mac-os-permissions.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Modal, ModalContainer } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { H2, Text } from "@amzn/stencil-react-components/text";
import { useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Logger } from "@/apis/Logger";
import { routePermissionGrant } from "@/helpers/electron-helper";
import {
  emitForwardedAfterReport,
  emitGrantsContinued,
  emitGrantsProbed,
  emitGrantsResolved,
  emitManualSetupBegun,
  emitPermissionsReportCancelled,
  emitPermissionsReportSubmitted,
  emitPermissionsReportSuperseded,
  emitSettingsOpened,
  emitSettingsOpenFailed,
} from "@/helpers/mac-permissions-metrics";
import { SnowplowTracker } from "@/helpers/snowplow";
import { useGrantStatus } from "@/hooks/useGrantStatus";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  type CapabilityBlockReason,
  type GiveUpReason,
  type ReportCause,
} from "@/types/agentcore";

import { type PermissionStep, PERMISSION_STEPS } from "./constants";
import {
  clearGrantAttempt,
  markGrantAttempt,
  readGrantAttempt,
} from "./grant-attempt";
import {
  clearGrantsRefused,
  hasRefusedGrants,
  markGrantsRefused,
} from "./grant-refusal";
import { MANUAL_HEADING_ID, ManualSetupModal } from "./ManualSetupModal";
import { PermissionStepRow } from "./PermissionStepRow";
import { REPORT_HEADING_ID, ReportIssueModal } from "./ReportIssueModal";

interface MacOsPermissionsScreenProps {
  /** Called once both grants are held and the hire chooses to go on. */
  readonly onContinue: () => void;
  /** Called when the hire gives up on the grants, so the caller drops to manual setup. */
  readonly onGiveUp: () => void;
}

/**
 * Walks the hire through the two macOS grants the assistant cannot work without.
 *
 * macOS gives no way to grant a permission programmatically and no notification when one changes,
 * so this can only point at System Settings and re-read the answer when the hire comes back.
 */
export function MacOsPermissionsScreen({
  onContinue,
  onGiveUp,
}: MacOsPermissionsScreenProps): JSX.Element {
  const intl = useIntl();
  const { didProbeAnswer, isResolved, missingGrants, refresh } =
    useGrantStatus();
  const [isReportOpen, setIsReportOpen] = useState(false);
  /*
   * Why the hire is off the walkthrough, latched rather than re-derived. Derived from the live probe
   * instead, a later answer changes the reason under them: the copy flips to the after-a-report
   * wording for someone who filed nothing, and a probe that fails drops them back onto the steps.
   */
  const [giveUp, setGiveUp] = useState<GiveUpReason | undefined>(undefined);
  /*
   * Named so a screen reader hears which grant landed. The region stays mounted and only its text
   * changes, because assistive tech often says nothing when one appears with content already in it.
   */
  const [announcement, setAnnouncement] = useState("");
  const previousHeld = useRef<readonly CapabilityBlockReason[] | undefined>(
    undefined
  );
  const hasCountedProbe = useRef(false);
  /* What was outstanding on arrival, so a resolution can be told from arriving already granted. */
  const arrivedOutstanding = useRef(false);
  const hasLatchedArrival = useRef(false);
  /* Which hire the carried record was read for, since the tracker resolves one after the first probe. */
  const latchedArrivalForPerson = useRef<string | undefined>(undefined);
  const hasCountedResolution = useRef(false);
  const settingsTrips = useRef(0);
  const openSettingsRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const { dispatch } = useContext(AppContext);
  /*
   * The person every record on this screen is keyed by. Read once, because the tracker resolves it
   * asynchronously: read again per call site, an attempt and the refusal that follows it could be
   * filed against two different people, and neither would then be found by the other.
   */
  const personId = SnowplowTracker.userId;

  /*
   * This screen stands in for the whole wizard, so support has to be told the hire is on it. Left
   * alone, the handoff still reports step 1 and reads as an identity-verification problem.
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-mac-permissions",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The hire is away in System Settings when a grant lands, so the checklist must speak.
   *
   * Compared on what the rows render as held, not on the missing list alone: a first probe that
   * failed reports nothing missing while both rows show outstanding, so a later answer of "both held"
   * moves no entry on that list and the one moment the region exists for went unannounced.
   *
   * Nothing is compared until the screen has rendered something, because the baseline has to be what
   * the hire was actually shown. Taken during the spinner frame it is empty, and the first real answer
   * then reads a grant they arrived already holding as one that just landed.
   */
  useEffect(() => {
    if (!isResolved) {
      return;
    }
    const isHeld = (step: (typeof PERMISSION_STEPS)[number]): boolean =>
      didProbeAnswer && !missingGrants.includes(step.reasonId);
    const before = previousHeld.current;
    previousHeld.current = PERMISSION_STEPS.filter(isHeld).map(
      (step) => step.reasonId
    );
    if (!before) {
      return;
    }
    const landed = PERMISSION_STEPS.filter(
      (step) => !before.includes(step.reasonId) && isHeld(step)
    );
    if (landed.length === 0) {
      /* A grant the hire has since revoked must stop being announced as on. */
      const hasRegressed = PERMISSION_STEPS.some(
        (step) => before.includes(step.reasonId) && !isHeld(step)
      );
      if (hasRegressed) {
        setAnnouncement("");
      }
      return;
    }
    const text = landed
      .map((step) =>
        intl.formatMessage(
          { id: "macPermissions-grant-landed-announcement" },
          { permission: intl.formatMessage({ id: step.paneNameId }) }
        )
      )
      .join(" ");
    setAnnouncement(text);
  }, [didProbeAnswer, intl, isResolved, missingGrants]);

  /*
   * A grant landing unmounts the walkthrough that was showing it, and with it the button the hire had
   * just pressed to get to System Settings -- so focus falls to the document and a keyboard hire is
   * returned to the top of a page whose next control has moved.
   *
   * Keyed on the walkthrough actually moving on, not on focus sitting at the document. The probe
   * re-runs on every window focus and publishes a fresh answer each time, so an unchanged re-probe
   * still renders -- and resting at the document is also where a screen-reader user reading with a
   * virtual cursor sits. Either would have this take focus off a hire who never lost it.
   */
  const focusedForStep = useRef<CapabilityBlockReason | "none" | undefined>(
    undefined
  );
  useEffect(() => {
    if (!isResolved) {
      return;
    }
    const showing = nextOutstanding?.reasonId ?? "none";
    const before = focusedForStep.current;
    focusedForStep.current = showing;
    /* First frame establishes what is on screen; nothing was lost getting here. */
    if (before === undefined || before === showing) {
      return;
    }
    if (document.activeElement !== document.body) {
      return;
    }
    const recovered = isEveryGrantHeld
      ? nextButtonRef.current
      : openSettingsRef.current;
    recovered?.focus();
  });

  /*
   * Once per load: the probe re-runs on every focus, and each is not a new arrival.
   *
   * A probe that failed is reported and then still waited on, because the arrival it would have
   * recorded is the denominator of the whole funnel. Latching on the first probe to merely settle
   * loses it outright, and the resolution counted later then has no arrival to be measured against.
   */
  useEffect(() => {
    if (!isResolved || hasCountedProbe.current) {
      return;
    }
    hasCountedProbe.current = didProbeAnswer;
    emitGrantsProbed(missingGrants, didProbeAnswer);
  }, [didProbeAnswer, isResolved, missingGrants]);

  /*
   * Latched from the first probe that answered, not the first that resolved. A probe that failed
   * reports an empty list, so latching on that would record a blocked hire as having arrived granted
   * and no later answer could correct it.
   */
  useEffect(() => {
    if (!didProbeAnswer) {
      return;
    }

    /*
     * What this load can see for itself, latched on the first answer so a hire whose id never resolves
     * is still counted.
     */
    if (!hasLatchedArrival.current) {
      hasLatchedArrival.current = true;
      if (missingGrants.length > 0) {
        arrivedOutstanding.current = true;
      }
    }

    /*
     * The carried record is keyed by person, so it cannot be read until one is resolved -- and the
     * tracker resolves it after the probe can answer, so this is keyed on *who* rather than on whether
     * it has run. Latched on "have we run", the relaunch that macOS asks for reads the record against
     * nobody, finds nothing, and the conversion this screen exists for goes unrecorded.
     */
    if (
      personId === undefined ||
      latchedArrivalForPerson.current === personId
    ) {
      return;
    }
    latchedArrivalForPerson.current = personId;
    /*
     * Read before anything is written, because a stored attempt is what this hire was doing when the
     * app last closed -- which is what granting Screen Recording makes macOS ask them to do. Written
     * first, a fresh load's zero would overwrite the trips they had already made.
     */
    const carried = readGrantAttempt(personId);
    if (carried) {
      arrivedOutstanding.current = true;
      settingsTrips.current = carried.trips;
    }
    if (missingGrants.length > 0) {
      arrivedOutstanding.current = true;
      markGrantAttempt(personId, settingsTrips.current);
    }
  }, [didProbeAnswer, missingGrants, personId]);

  /*
   * A hire who reported a grant as unobtainable and has since switched it on is not still refusing, so
   * the assistant is offered again rather than held to an answer macOS has overtaken.
   */
  useEffect(() => {
    if (didProbeAnswer && missingGrants.length === 0) {
      clearGrantsRefused();
    }
  }, [didProbeAnswer, missingGrants]);

  /*
   * Nothing was outstanding when this opened, so there is nothing to walk. macOS can revoke a grant at
   * any time, which is why the walkthrough is offered on every load rather than remembered as done --
   * but a hire who granted both and then relaunched should not be made to read it and press Next again.
   *
   * Keyed on the arrival latch, not the live probe, so granting the last one mid-walkthrough still ends
   * on the confirmation the hire is waiting for.
   */
  const hasAutoContinued = useRef(false);
  useEffect(() => {
    if (
      !didProbeAnswer ||
      arrivedOutstanding.current ||
      hasAutoContinued.current
    ) {
      return;
    }
    hasAutoContinued.current = true;
    onContinue();
  }, [didProbeAnswer, onContinue]);

  /*
   * A restart re-opens this screen, because the answered flag is per-mount while the refusal is not.
   * The hire already told us they cannot grant these, so they go straight to the steps rather than
   * read the acknowledgement again -- its own copy points at those steps as what to do next.
   *
   * An undefined reason is what makes this a returning hire: a report filed in this sitting latches
   * one synchronously, so that hire still sees the acknowledgement. Not in the gate, which cannot
   * clear the record. The record is read here rather than at mount so the skip still fires for a hire
   * whose id resolves after the first render.
   */
  const hasSkippedAfterReport = useRef(false);
  useEffect(() => {
    if (
      giveUp !== undefined ||
      !didProbeAnswer ||
      missingGrants.length === 0 ||
      hasSkippedAfterReport.current ||
      !hasRefusedGrants(personId)
    ) {
      return;
    }
    hasSkippedAfterReport.current = true;
    clearGrantAttempt();
    emitForwardedAfterReport();
    onGiveUp();
  }, [didProbeAnswer, giveUp, missingGrants, onGiveUp, personId]);

  /*
   * A grant the host names but this page cannot explain -- a rename, or a third grant. In an effect
   * because a storage write during render is one React may repeat. `didProbeAnswer` is read too, since
   * a failed probe leaves the last list in place.
   */
  const hasUnexplainedGrant =
    didProbeAnswer &&
    missingGrants.length > 0 &&
    !missingGrants.some((grant) =>
      PERMISSION_STEPS.some((step) => step.reasonId === grant)
    );

  /*
   * Nothing is persisted. This is not a refusal, and writing the refusal record for it made the two
   * indistinguishable on the next load -- which of the two messages the hire saw then came down to
   * the order these effects happen to be declared in. Re-derived instead: the same host reporting the
   * same unrecognised grant reaches the same conclusion, without a record to be misread.
   */
  useEffect(() => {
    /*
     * A report the hire filed outranks this, and the check has to be the record rather than `giveUp`:
     * both effects run in the same commit and read the same undefined `giveUp`, so a guard on it alone
     * still leaves the winner down to declaration order. Without this, a hire who reported an issue
     * and whose host then named an unrecognised grant lost their acknowledgement to the
     * could-not-check wording, which reads as though they never told us.
     */
    if (
      giveUp !== undefined ||
      !hasUnexplainedGrant ||
      /*
       * And not before the tracker has resolved anybody: `hasRefusedGrants(undefined)` is false, so a
       * refused hire would latch here first and the guard above would then block the correction.
       */
      personId === undefined ||
      hasRefusedGrants(personId)
    ) {
      return;
    }
    clearGrantAttempt();
    setGiveUp("unexplained");
  }, [giveUp, hasUnexplainedGrant, personId]);

  /*
   * Counted once, and only for a hire who had something outstanding, so arriving granted is not a
   * win. `didProbeAnswer` is required as well: an empty list we assumed after a failed probe is not
   * evidence the hire granted anything.
   *
   * Must stay declared after the arrival latch: both run in the same commit, and this reads the
   * `arrivedOutstanding` the latch sets. Ordered the other way, every resolution goes uncounted.
   */
  useEffect(() => {
    if (
      !isResolved ||
      !didProbeAnswer ||
      missingGrants.length > 0 ||
      !arrivedOutstanding.current ||
      hasCountedResolution.current ||
      giveUp !== undefined
    ) {
      return;
    }
    hasCountedResolution.current = true;
    clearGrantAttempt();
    emitGrantsResolved(settingsTrips.current);
  }, [didProbeAnswer, giveUp, isResolved, missingGrants]);

  /* Nothing is claimed until the first probe answers, or a held grant would read as outstanding. */
  if (!isResolved) {
    return (
      <Spinner
        dataTestId="macOsPermissionsProbing"
        size={SpinnerSize.Medium}
        showText
      />
    );
  }

  /*
   * A probe that did not answer reports an empty list, which must not read as "both held": the hire
   * would be shown two green confirmations and waved past grants macOS never gave. Unable to say which
   * grant is missing, both are treated as outstanding, so the first is walked through and the report
   * route stays reachable.
   */
  const outstanding = didProbeAnswer
    ? PERMISSION_STEPS.filter((step) => missingGrants.includes(step.reasonId))
    : PERMISSION_STEPS;
  const nextOutstanding = outstanding[0];
  /*
   * Said in terms of the grants rather than of `nextOutstanding`, which coincides with it only
   * because the unrecognised-grant case returns below. Remove that return and an empty walkthrough
   * would once again read as "all set".
   */
  const isEveryGrantHeld = didProbeAnswer && missingGrants.length === 0;

  /** The one way on once the assistant is out of reach. */
  const beginManualSetup = (): void => {
    emitManualSetupBegun();
    onGiveUp();
  };

  /** Every dismissal lands here -- Cancel, the X, Escape, backdrop -- so each is counted once. */
  const closeReport = (): void => {
    setIsReportOpen(false);
    emitPermissionsReportCancelled();
    refresh();
  };

  /**
   * The grants can land while the dialog is open, so a report is not always a dead end.
   *
   * Keyed on `isEveryGrantHeld` rather than on the list alone: a probe that never answered reports
   * nothing missing, and reading that as "landed" makes Submit a no-op on a screen with no Next.
   */
  const giveUpUnlessGrantsLanded = (cause: ReportCause): void => {
    setIsReportOpen(false);
    if (isEveryGrantHeld) {
      /* The cause still counts: it names macOS behaviour a hire hit and then recovered from. */
      emitPermissionsReportSuperseded(cause);
      return;
    }
    emitPermissionsReportSubmitted(cause);
    markGrantsRefused(personId);
    clearGrantAttempt();
    setGiveUp("reported");
  };

  /**
   * Counted on a surface actually opening, not on the click.
   *
   * The result says only that a surface came up, never that a grant was given -- but a trip that
   * never happened must not be counted as one, or the attempts behind a resolution are wrong.
   */
  /*
   * Said out loud as well as counted, because the button is the one instruction on this screen and a
   * click that silently does nothing reads as a broken page. Points at the manual route already
   * rendered below it, which is the same sentence, rather than introducing a second way of saying it.
   */
  const announceSettingsDidNotOpen = (step: PermissionStep): void =>
    setAnnouncement(
      intl.formatMessage(
        { id: "macPermissions-fallback-intro" },
        { permission: intl.formatMessage({ id: step.paneNameId }) }
      )
    );

  /* Takes the step rather than the reason id, so the pane's name needs no lookup to fail back to. */
  const openSettings = (step: PermissionStep): void => {
    const grant = step.reasonId;
    void (async () => {
      try {
        const surface = await routePermissionGrant(grant);
        /* Only `none` means the click opened nothing; a dialog routed the hire just as a pane does. */
        if (surface === "none") {
          emitSettingsOpenFailed(grant);
          announceSettingsDidNotOpen(step);
          return;
        }
        settingsTrips.current += 1;
        markGrantAttempt(personId, settingsTrips.current);
        emitSettingsOpened(grant, surface);
      } catch (error) {
        void Logger.errorErrorLike("Opening the permission pane threw", error);
        emitSettingsOpenFailed(grant);
        announceSettingsDidNotOpen(step);
      }
    })();
  };

  return (
    <Col
      gridGap="S400"
      className="mac-permissions-page"
      dataTestId="macOsPermissionsScreen"
    >
      <Col gridGap="S100">
        {/* A real heading, like the consent takeover: this replaces the wizard, so heading
            navigation is how a screen-reader user finds out where they are. */}
        <H2 fontWeight="bold">
          <FormattedMessage id="macPermissions-page-title" />
        </H2>
        <Text fontWeight="regular">
          <FormattedMessage id="macPermissions-page-description" />
        </Text>
      </Col>

      <Hr color="neutral20" size="narrow" />

      <View
        role="status"
        aria-live="polite"
        className="mac-permissions-announcement"
      >
        {announcement}
      </View>

      {PERMISSION_STEPS.map((step, index) => (
        <PermissionStepRow
          key={step.reasonId}
          step={step}
          position={index + 1}
          total={PERMISSION_STEPS.length}
          isGranted={didProbeAnswer && !missingGrants.includes(step.reasonId)}
          isCurrent={step.reasonId === nextOutstanding?.reasonId}
          onOpenSettings={() => openSettings(step)}
          onReportIssue={() => setIsReportOpen(true)}
          openSettingsRef={
            step.reasonId === nextOutstanding?.reasonId
              ? openSettingsRef
              : undefined
          }
        />
      ))}

      {isEveryGrantHeld && (
        <>
          <Text fontWeight="regular" dataTestId="macOsPermissionsAllSet">
            <FormattedMessage id="macPermissions-all-set" />
          </Text>
          <Row gridGap="S400" justifyContent="flex-end">
            <Button
              ref={nextButtonRef}
              variant={ButtonVariant.Primary}
              onClick={() => {
                emitGrantsContinued();
                onContinue();
              }}
              dataTestId="macOsPermissionsNextButton"
            >
              <FormattedMessage id="macPermissions-next-button" />
            </Button>
          </Row>
        </>
      )}

      {/* Remounted per opening, so the dialog's mount effect counts every opening rather than the first. */}
      {isReportOpen && (
        <Modal isOpen close={closeReport} aria-labelledby={REPORT_HEADING_ID}>
          <ModalContainer>
            <ReportIssueModal
              onClose={closeReport}
              onSubmitted={giveUpUnlessGrantsLanded}
            />
          </ModalContainer>
        </Modal>
      )}

      {/*
       * `close` goes on rather than back: the grants are required, so dismissing this would leave the
       * hire on a walkthrough they have already said they cannot finish.
       */}
      {giveUp !== undefined && (
        <Modal
          isOpen
          close={beginManualSetup}
          aria-labelledby={MANUAL_HEADING_ID}
        >
          <ModalContainer>
            <ManualSetupModal
              hasReported={giveUp === "reported"}
              onBegin={beginManualSetup}
            />
          </ModalContainer>
        </Modal>
      )}
    </Col>
  );
}
